// Azure Timekeeping Service - Speichert Zeiteinträge in timekeeping-Tabelle

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "timekeeping",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Extrahiere Code aus ContractNoteName
// Die ersten 3 Zeichen sind der Code
const extractCodeFromContractName = (contractName) => {
  if (!contractName) return "";
  return contractName.substring(0, 3).trim();
};

export const saveTimekeepingEntry = async (
  employeeName,
  employeeUuid,
  entryData,
  activityTypeName,
  source = "abaninja"
) => {
  const now = new Date().toISOString();
  const rowKey = `${employeeUuid}_${
    entryData.date
  }_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='timekeeping',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const isFullDay = entryData.isFullDay === true;

  // Berechne Duration (Ganztag: 0/0, Gleitzeit-Semantik bleibt unverändert)
  const durationHours =
    !isFullDay && entryData.startTime && entryData.endTime
      ? calculateDuration(entryData.startTime, entryData.endTime)
      : { hours: 0, minutes: 0 };

  // Code = fullReference (erste 3 Zeichen der Reference)
  const code = entryData.fullReference
    ? entryData.fullReference.substring(0, 3).trim()
    : "";

  const entity = {
    PartitionKey: "timekeeping",
    RowKey: rowKey,
    EmployeeUuid: employeeUuid,
    EmployeeName: employeeName || "",
    Date: entryData.date || "",
    AddressName: entryData.addressName || "",
    Comment: entryData.description || "",
    ContractNoteName: entryData.contractName || "",
    Code: code,
    DurationHours: durationHours.hours,
    DurationMinutes: durationHours.minutes,
    StartTime: entryData.startTime || "",
    EndTime: entryData.endTime || "",
    TypeName: activityTypeName || "",
    IsFullDay: isFullDay,
    AbsenceGroupId: entryData.absenceGroupId || "",
    Source: source,
    CreatedAt: now,
    UpdatedAt: now,
  };

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Fehler beim Speichern in timekeeping:", errorText);
    throw new Error(`Azure Table Error: ${response.status}`);
  }

  return true;
};

const convertDateFormat = (dateString) => {
  if (!dateString) return "";

  // Prüfe ob bereits im richtigen Format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Konvertiere DD.MM.YYYY zu YYYY-MM-DD
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split(".");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  console.warn("Unbekanntes Datumsformat:", dateString);
  return dateString;
};

// Hilfsfunktion: Berechne Dauer
const calculateDuration = (startTime, endTime) => {
  const [startHour, startMin] = startTime.split(":").map(Number);
  const [endHour, endMin] = endTime.split(":").map(Number);

  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  const totalMinutes = endMinutes - startMinutes;

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
};

// Timekeeping-Daten für historische Auswertung laden
// Optional: projectCode filters server-side to avoid loading all entries
export const getTimekeepingData = async (dateFrom = null, dateTo = null, projectCode = null) => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;
    let filter = "";

    const conditions = [];
    if (dateFrom && dateTo) {
      conditions.push(`Date ge '${dateFrom}' and Date le '${dateTo}'`);
    }
    if (projectCode) {
      conditions.push(`Code eq '${projectCode}'`);
    }
    if (conditions.length > 0) {
      filter = `&$filter=${conditions.join(' and ')}`;
    }

    let allData = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}?${AZURE_CONFIG.sasToken}${filter}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();

      const mappedData = data.value.map((item) => ({
        employeeUuid: item.EmployeeUuid,
        employeeName: item.EmployeeName,
        date: convertDateFormat(item.Date),
        startTime: item.StartTime || "",
        endTime: item.EndTime || "",
        durationHours: item.DurationHours || 0,
        durationMinutes: item.DurationMinutes || 0,
        typeName: item.TypeName || "",
        contractNoteName: item.ContractNoteName || "",
        code: item.Code || "",
        comment: item.Comment || "",
        isFullDay: item.IsFullDay === true,
        absenceGroupId: item.AbsenceGroupId || "",
        source: item.Source || "abaninja",
      }));

      allData = allData.concat(mappedData);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");

    } while (nextPartitionKey && nextRowKey);

    return allData;
  } catch (error) {
    console.error("Fehler beim Laden der Timekeeping-Daten:", error);
    return [];
  }
};

// Lokale Zeiteinträge für einen Mitarbeiter in einem Zeitraum laden
export const getLocalTimekeepingEntries = async (employeeUuid, dateFrom, dateTo) => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;
    const filter = `&$filter=PartitionKey eq 'timekeeping' and EmployeeUuid eq '${employeeUuid}' and Source eq 'local' and Date ge '${dateFrom}' and Date le '${dateTo}'`;

    let allData = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}?${AZURE_CONFIG.sasToken}${filter}`;

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();

      const mappedData = (data.value || []).map((item) => ({
        uuid: item.RowKey,
        employeeUuid: item.EmployeeUuid,
        date: convertDateFormat(item.Date),
        startTime: item.StartTime || "",
        endTime: item.EndTime || "",
        durationHours: item.DurationHours || 0,
        durationMinutes: item.DurationMinutes || 0,
        description: item.Comment || "",
        comment: item.Comment || "",
        contractUuid: "",
        contractTitle: item.ContractNoteName || "",
        contractNoteName: item.ContractNoteName || "",
        code: item.Code || "",
        activityTypeUuid: "",
        typeName: item.TypeName || "",
        isFullDay: item.IsFullDay === true,
        absenceGroupId: item.AbsenceGroupId || "",
        isPause: false,
        source: "local",
      }));

      allData = allData.concat(mappedData);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allData;
  } catch (error) {
    console.error("Fehler beim Laden lokaler Timekeeping-Einträge:", error);
    return [];
  }
};

// Alle Einträge einer Person von einer alten auf die kanonische EmployeeUuid
// umhängen (ADR 0003). Ändert per MERGE nur EmployeeUuid/UpdatedAt, RowKey und
// übrige Felder bleiben erhalten. dryRun liefert nur die betroffenen Rohentitäten.
export const reassignTimekeepingEmployee = async (oldUuid, newUuid, { dryRun = true } = {}) => {
  if (!oldUuid || !newUuid || oldUuid === newUuid) {
    throw new Error("reassignTimekeepingEmployee: ungültige IDs");
  }
  const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;
  const filter = `PartitionKey eq 'timekeeping' and EmployeeUuid eq '${oldUuid}'`;

  let entities = [];
  let nextPartitionKey = null;
  let nextRowKey = null;
  do {
    let url = `${baseUrl}?${AZURE_CONFIG.sasToken}&$filter=${encodeURIComponent(filter)}`;
    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!response.ok) {
      throw new Error(`Azure Table Error: ${response.status}`);
    }
    const data = await response.json();
    entities = entities.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);

  if (dryRun) return { count: entities.length, entities };

  const now = new Date().toISOString();
  for (const entity of entities) {
    const url = `${baseUrl}(PartitionKey='timekeeping',RowKey='${encodeURIComponent(entity.RowKey)}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify({ EmployeeUuid: newUuid, UpdatedAt: now }),
    });
    if (!response.ok) {
      throw new Error(`Azure Merge Error ${response.status} bei ${entity.RowKey}`);
    }
  }
  return { count: entities.length, entities };
};

// Alle Einträge einer Ganztag-Abwesenheitsgruppe (z.B. Ferien Mo–Fr) löschen
export const deleteTimekeepingEntriesByGroup = async (
  employeeUuid,
  absenceGroupId
) => {
  if (!absenceGroupId) return true;
  try {
    const filter = `PartitionKey eq 'timekeeping' and EmployeeUuid eq '${employeeUuid}' and AbsenceGroupId eq '${absenceGroupId}'`;
    const searchUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=${encodeURIComponent(filter)}`;

    const searchResponse = await fetch(searchUrl, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!searchResponse.ok) {
      throw new Error(`Azure Search Error: ${searchResponse.status}`);
    }

    const data = await searchResponse.json();

    const deletePromises = (data.value || []).map(async (entry) => {
      const deleteUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='timekeeping',RowKey='${entry.RowKey}')?${AZURE_CONFIG.sasToken}`;
      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: { "If-Match": "*" },
      });
      if (!response.ok && response.status !== 404) {
        throw new Error(`Azure Delete Error: ${response.status}`);
      }
      return true;
    });

    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen der Abwesenheitsgruppe:", error);
    throw error;
  }
};

// Timekeeping-Einträge für bestimmtes Datum und Employee löschen
export const deleteTimekeepingEntries = async (
  employeeUuid,
  date,
  startTime,
  endTime
) => {
  try {
    // Suche alle passenden Einträge
    const filter = `PartitionKey eq 'timekeeping' and EmployeeUuid eq '${employeeUuid}' and Date eq '${date}' and StartTime eq '${startTime}' and EndTime eq '${endTime}'`;
    const searchUrl = `https://${
      AZURE_CONFIG.storageAccount
    }.table.core.windows.net/${AZURE_CONFIG.tableName}?${
      AZURE_CONFIG.sasToken
    }&$filter=${encodeURIComponent(filter)}`;

    const searchResponse = await fetch(searchUrl, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!searchResponse.ok) {
      throw new Error(`Azure Search Error: ${searchResponse.status}`);
    }

    const data = await searchResponse.json();

    // Lösche alle gefundenen Einträge
    const deletePromises = data.value.map(async (entry) => {
      const deleteUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='timekeeping',RowKey='${entry.RowKey}')?${AZURE_CONFIG.sasToken}`;

      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "If-Match": "*",
        },
      });

      if (!response.ok && response.status !== 404) {
        throw new Error(`Azure Delete Error: ${response.status}`);
      }

      return true;
    });

    await Promise.all(deletePromises);

    return true;
  } catch (error) {
    console.error("Fehler beim Löschen aus timekeeping:", error);
    throw error;
  }
};
